// src/pages/onboarding/get-started.tsx
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  Clock,
  Shield,
  Star,
  Eye,
  TrendingUp,
  Zap,
  Lightbulb,
} from "lucide-react";
import heroDashboard from "/images/insights.png";

const benefits = [
  {
    icon: Eye,
    gradient: "from-purple-500 to-pink-500",
    title: "See Which Campaigns Are Draining Your Budget",
    description:
      "Instantly identify where your money is going to waste and which campaigns need immediate attention.",
  },
  {
    icon: TrendingUp,
    gradient: "from-blue-500 to-cyan-500",
    title: "Get Specific Fixes You Can Implement Today",
    description:
      "Actionable recommendations that you can apply right now to improve your campaign performance.",
  },
  {
    icon: Zap,
    gradient: "from-emerald-500 to-teal-500",
    title: "Discover Hidden Settings Costing You Money",
    description:
      "Find risky settings, mismatches, and account issues that are bleeding your budget.",
  },
  {
    icon: Lightbulb,
    gradient: "from-amber-500 to-orange-500",
    title: "Your Data Stays in Your Control",
    description:
      "Everything is stored in your own Google Sheet, not on our servers. You own your data completely.",
  },
];

const testimonials = [
  {
    name: "Sara Chen",
    role: "Marketing Director",
    text: "I love showing my clients this tool because I can show them the same data in ways they've never seen before.",
  },
  {
    name: "Mike Rodriguez",
    role: "E-commerce Owner",
    text: "I can't believe how many insights I can get from just one tool.",
  },
  {
    name: "Lisa Park",
    role: "Agency Owner",
    text: "Frankly this tool should be illegal - or cost $1000 per month! It's so good.",
  },
];

export default function OnboardingGetStartedPage() {
  return (
    <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-0 w-64 h-64 bg-success/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="relative z-10 px-6 py-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-primary-light/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-primary/10">
              <Star className="w-4 h-4 text-primary" />
              <span className="text-primary font-medium text-sm">
                Trusted by 2,742+ advertisers
              </span>
            </div>

            <h1 className="text-6xl md:text-7xl font-bold leading-tight mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Your Google Ads Could Be
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                {" "}
                Performing Better.
              </span>
              <br />
              Want to See Exactly How?
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Get a personalized optimization report in 4 minutes. My agency
              audited hundreds of accounts, we found that most could save
              hundreds, if not thousands, of dollars per month. Check yours now.
              <br />
              Brought to you by Mike Rhodes, creator of the PMax Script.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-16 items-start">
            {/* Left Content: Benefits */}
            <div className="lg:col-span-7 space-y-8">
              <div className="grid gap-6">
                {benefits.map(
                  ({ icon: Icon, gradient, title, description }, index) => (
                    <div
                      key={title}
                      className="group flex items-center gap-6 animate-scale-in"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div
                        className={`flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-r ${gradient} flex items-center justify-center shadow-medium group-hover:scale-110 transition-transform duration-300`}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </div>

                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                          {title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {description}
                        </p>
                      </div>
                    </div>
                  ),
                )}
              </div>

              {/* Trust Indicators - Centered */}
              <div
                className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground animate-fade-in"
                style={{ animationDelay: "600ms" }}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-success" />
                  <span>Takes less than 4 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-success" />
                  <span>Privacy protected</span>
                </div>
              </div>

              {/* CTA Button */}
              <div
                className="animate-fade-in flex justify-center"
                style={{ animationDelay: "400ms" }}
              >
                <Button
                  asChild
                  variant="default"
                  size="big"
                  className="w-full sm:w-auto"
                >
                  <Link to="/onboarding/start">Connect My Account</Link>
                </Button>
              </div>
            </div>

            {/* Right Side: Hero Image + Testimonials */}
            <div className="lg:col-span-5 space-y-8">
              {/* Loom Video */}
              <div
                className="relative group animate-scale-in"
                style={{ animationDelay: "400ms" }}
              >
                <div className="absolute -inset-4 bg-gradient-primary rounded-3xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500" />
                <div className="relative overflow-hidden rounded-2xl shadow-large border border-border/50 bg-gradient-card backdrop-blur-sm">
                  <div
                    style={{
                      position: "relative",
                      paddingBottom: "56.25%",
                      height: 0,
                    }}
                  >
                    <iframe
                      src="https://www.loom.com/embed/3dfe9c32eba047a5a4f76864088e4d90?sid=8f4c4c8f-3f8f-4f8f-8f8f-8f8f8f8f8f8f"
                      frameBorder="0"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        borderRadius: "1rem",
                      }}
                      title="Google Ads Optimization Demo"
                    />
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 pointer-events-none">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                      <span className="text-white text-sm font-medium">
                        Let me explain the 3 steps to get setup
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Testimonials */}
              <div
                className="space-y-4 animate-fade-in"
                style={{ animationDelay: "500ms" }}
              >
                <h3 className="text-lg font-semibold text-foreground">
                  What 2700+ Happy Users Say
                </h3>
                {testimonials.map((testimonial) => (
                  <div
                    key={testimonial.name}
                    className="relative p-6 rounded-2xl bg-gradient-card backdrop-blur-sm border border-border/50 shadow-soft hover:shadow-medium transition-all duration-300"
                  >
                    <div className="flex items-start gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-4 h-4 text-amber-400 fill-current"
                        />
                      ))}
                    </div>
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div>
                      <p className="font-medium text-foreground">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Privacy Note - Centered and Lower */}
          <div
            className="text-center mt-16 animate-fade-in"
            style={{ animationDelay: "900ms" }}
          >
            <div className="max-w-xl mx-auto">
              <div className="text-xs text-muted-foreground bg-muted/50 rounded-xl p-4">
                <div className="text-center">
                  <p className="font-medium text-foreground mb-1">
                    <Shield className="w-4 h-4 text-success inline mr-2" />
                    We take privacy seriously.
                  </p>
                  <p>
                    Your data is stored in a Google Sheet that you control, not
                    on our servers.
                  </p>
                  <p>
                    Read our{" "}
                    <Link
                      to="/privacy"
                      className="text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
                    >
                      privacy policy
                    </Link>{" "}
                    to learn more.
                  </p>
                </div>
              </div>

              {/* Authentication Links - Moved under privacy card */}
              <div
                className="text-sm text-muted-foreground mt-4 animate-fade-in"
                style={{ animationDelay: "1000ms" }}
              >
                Already have an account?{" "}
                <Link
                  to="/sign-in"
                  className="text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
